import { forwardRef, memo, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { fetchPreShipmentLists } from '../../store/slices/dealDropdownOptions';
import { formatDateFields } from '../../utils/dateUtils';
import useSessionExpire from '../../hooks/useSessionExpire';
import UnfoldLessIcon from '@mui/icons-material/UnfoldLess';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import VirtualizedAutocomplete from '../../components/Autocomplete';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DealHeader from './DealHeader';
import BuyingLeg from './BuyingLeg';
import SellingLeg from './SellingLeg';
import Modal from 'react-bootstrap/Modal';
import Grid from '@mui/material/Grid';
import Button from '@mui/material/Button';
import dayjs from 'dayjs';
import axios from 'axios';
import { Tooltip } from '@mui/material';
import { abortController } from '../../utils/abortController';
import useTab from '../../hooks/useTab';
import LinkedDealOverridePopup from './LinkedDealOverridePopup';
import BuyingPaymentTerms from './PaymentTerms/BuyingPaymentTerms';
import SellingPaymentTerms from './PaymentTerms/SellingPaymentTerms';

const PreShipment = forwardRef(({ isWritable = false, isLoading = false, isPeriodLocked = false, isFertilizersAndPetroChemicalsDeal = false, isFoodsOrPolymersDeal = false, isFoodsPolymersOrSBLCDeal = false, isSBLCDeal = false, selectedBusinessUnit = null, selectedDeal = null, selectedPFI = null, setIsUpdated = () => { }, setIsPeriodLocked = () => { }, selectedTab = null, setSelectedTab = () => { }, setActiveTab = () => { }, setIsLoading = () => { }, setShowAlert = () => { }, setAlertMessage = () => { }, setAlertType = () => { }, logOut = () => { } }, ref) => {

  const {
    isNewPFI = false,
    setSelectedPFI = () => { },
    setSelectedLinkedPFI = () => { },
    selectedLinkedDeal = null,
  } = useTab();

  const { pathname } = useLocation();
  const dispatch = useDispatch();

  /* SESSION EXPIRY VARIABLES */
  const triggerSessionExpire = useSessionExpire();

  /* LIST VARIABLES */
  const preShipmentListData = useSelector((state) => state.dealDropdownOptions.preShipment);
  const [isPreShipmentListDataLoading, setIsPreShipmentListDataLoading] = useState(false);
  const [selectedDealPfiList, setSelectedDealPfiList] = useState([]);
  const [selectedLinkedDealPfiList, setSelectedLinkedDealPfiList] = useState([]);
  const [productData, setProductData] = useState([]);
  const [customerCounterParties, setCustomerCounterParties] = useState([]);
  const [supplierCounterParties, setSupplierCounterParties] = useState([]);
  const [paymentTermTypes, setPaymentTermTypes] = useState([]);


  // ENTITY VARIABLES 
  const [entitiesForCopy, setEntitiesForCopy] = useState([]);
  const [isEntitiesLoading, setIsEntitiesLoading] = useState(false);
  const [selectedEntity, setSelectedEntity] = useState(null);

  /* AUTO-POPULATE DATA VARIABLES */
  const [isDealsListForCopyLoading, setisDealsListForCopyLoading] = useState(false);
  const [dealListForAutoPopulate, setDealListForAutoPopulate] = useState([]);
  const [selectedDealForAutopopulate, setSelectedDealForAutopopulate] = useState(selectedDeal);
  const [isPFIsListForCopyLoading, setisPFIsListForCopyLoading] = useState(false);
  const [pfisForCopy, setPFIsForCopy] = useState([]);
  const [selctedPFIForAutoPopulate, setSelctedPFIForAutoPopulate] = useState(null);

  /* PERIOD LOCK VARIABLES */
  const [lockedFields, setLockedFields] = useState([]);

  /* FORM VARIABLES */
  const [expandedFormSections, setExpandedFormSections] = useState([1, 2, 3, 4, 5, 6, 7]);

  /* Deal Header Variables */
  const dealHeaderDataSchema = {
    Deal_PFI_Id: null,
    Order_Status: "",
    Buyer_Id: null,
    Buying_Payment_Terms_Id: null,
    Buying_Tenor_Id: null,
    Selling_Payment_Terms_Id: null,
    Selling_Tenor_Id: null,
    Seller_Id: null,
    Booked_for_Month: null,
    Order_Date: null,
    Deal_Close_Date: null,
    Deal_Sheet_Approval_Date: null,
    Shipment_Month_And_Date: null,
    Deal_Cancelled_Reason: "",
    Total_Amount: 0,
    Freight: null,
    FOB_Value: null,
    Label: "",
    Value: null,
    Transaction_Type: null,
    Trade_Finance_Deal: null,
    tranche_product_exists: false,
    products: []
  };
  const [isExistingDealHeader, setIsExistingDealHeader] = useState(false);
  const [isDealHeaderFormUpdated, setIsDealHeaderFormUpdated] = useState(false);
  const [isDealHeaderFormSavedOrDeleted, setIsDealHeaderFormSavedOrDeleted] = useState(false);
  const [dealHeaderInitialFormData, setDealHeaderInitialFormData] = useState(structuredClone(dealHeaderDataSchema));
  const [dealHeaderFormData, setDealHeaderFormData] = useState(structuredClone(dealHeaderDataSchema));
  const [isDealHeaderFormDataLoaded, setIsDealHeaderFormDataLoaded] = useState(false);

  /* Buying Leg Variables */
  const buyingLegDataSchema = {
    Deal_PFI_Id: null,
    Trader_Id: null,
    FZE_Bank_Id: null,
    Buying_Tenor_Id: null,
    Supplier_Bank_Id: null,
    LC_Confirming_Bank_Id: null,
    Advise_Through_Bank: "",
    Buying_Currency_Id: null,
    Latest_Shipment_Date: null,
    LC_Expiry_Date: null,
    Port_of_Loading_Id: null,
    Supplier_PFI_No: "",
    Supplier_PFI_Date: null,
    Supplier_CI_No: "",
    Supplier_CI_Date: null,
    PO_No: "",
    PO_Date: null,
    Incoterm_Id: null,
    Remaining_Balance_Payment_Days: null,
    Payment_Instrument_Status: null,
    LC_No: "",
    LC_Issue_Date: null,
    Document_Presentation_Days: null,
    Special_Requirement: ""
  };
  const [isExistingBuyingLeg, setIsExistingBuyingLeg] = useState(false);
  const [isBuyingLegFormUpdated, setIsBuyingLegFormUpdated] = useState(false);
  const [isBuyingLegFormSavedOrDeleted, setIsBuyingLegFormSavedOrDeleted] = useState(false);
  const [buyingLegInitialFormData, setBuyingLegInitialFormData] = useState(structuredClone(buyingLegDataSchema));
  const [buyingLegFormData, setBuyingLegFormData] = useState(structuredClone(buyingLegDataSchema));

  /* Selling Leg Variables */
  const sellingLegDataSchema = {
    Deal_PFI_Id: null,
    Trader_Id: null,
    Selling_Currency_Id: null,
    Incoterm_Id: null,
    Drawee_Bank: "",
    Customer_Bank_Id: null,
    Customer_Bank_Name: "",
    LC_Confirming_Bank_Id: null,
    Customer_Address: "",
    Ship_To_Party_Same_As_Customer: null,
    Ship_To_Party_Id: null,
    Ship_To_Party_Address: "",
    Port_of_Discharge_Id: null,
    PFI_Issuance_Date: null,
    Validity_Days: null,
    PFI_Expiry_Date: null,
    Last_Shipment_Date: null,
    Remaining_Balance_Payment_Days: null,
    Payment_Instrument_Status: null,
    LC_No: "",
    Issuance_Date: null,
    LC_Expiry_Date: null,
    Document_Presentation_Days: null,
    Terms_and_Conditions: "",
    Country_Of_Origin: "",
    Packaging_Type_Id: null
  };
  const [isExistingSellingLeg, setIsExistingSellingLeg] = useState(false);
  const [isSellingLegFormUpdated, setIsSellingLegFormUpdated] = useState(false);
  const [isSellingLegFormSavedOrDeleted, setIsSellingLegFormSavedOrDeleted] = useState(false);
  const [sellingLegInitialFormData, setSellingLegInitialFormData] = useState(structuredClone(sellingLegDataSchema));
  const [sellingLegFormData, setSellingLegFormData] = useState(structuredClone(sellingLegDataSchema));

  // PAYMENT TERMS FORM DATA 
  const [isBuyingPaymentTermsFormUpdated, setIsBuyingPaymentTermsFormUpdated] = useState(false);
  const [isSellingPaymentTermsFormUpdated, setIsSellingPaymentTermsFormUpdated] = useState(false);

  // LINKED DEAL FORM DATA
  const linkedDealFormDataSchema = {
    Deal_PFI_Id: null,
    Buyer_Id: null,
    Customer_Address: "",
    Seller_Id: null,
    products: [],
    Buying_Payment_Terms_Id: null,
    Selling_Payment_Terms_Id: null,
    Buying_Tenor_Id: null,
    Selling_Tenor_Id: null,
  }
  const [isExistinglinkedDeal, setIsExistingLinkedDeal] = useState(false);
  const [isLinkedDealFormUpdated, setIsLinkedDealFormUpdated] = useState(false);
  const [linkedDealInitialFormData, setLinkedDealInitialFormData] = useState(structuredClone(linkedDealFormDataSchema));
  const [linkedDealFormData, setLinkedDealFormData] = useState(structuredClone(linkedDealFormDataSchema));
  const [isLinkedThrougRoute, setIsLinkedThrougRoute] = useState(false);
  const [showLinkedDealOverridePopup, setShowLinkedDealOverridePopup] = useState(false);
  const [linkedOverwrittenFormData, setLinkedOverwrittenFormData] = useState(null);

  /* SBLC Variables */
  const sblcProductGradeDataSchema = {
    Deal_PFI_Id: null,
    Tranche_Id: null,
    SBLC_Id: null,
    PFIs: [],
    PFI_Id: []
  };
  const [isExistingSBLC, setIsExistingSBLC] = useState(false);
  const [isSBLCFormUpdated, setIsSBLCFormUpdated] = useState(false);
  const [sblcInitialFormData, setSBLCInitialFormData] = useState(structuredClone(sblcProductGradeDataSchema));
  const [sblcFormData, setSBLCFormData] = useState(structuredClone(sblcProductGradeDataSchema));

  /* DIALOG VARIABLES */
  const [warningDialogText, setWarningDialogText] = useState(null);
  const [showWarningDialog, setShowWarningDialog] = useState(false);

  /* MODAL VARIABLES */
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  /* REFERENCE VARIABLES */
  const dealHeaderRef = useRef(null);
  const buyingLegRef = useRef(null);
  const sellingLegRef = useRef(null);
  const buyingPaymentTermsRef = useRef(null);
  const sellingPaymentTermsRef = useRef(null);

  const handleLinkedDealOverrideSubmit = (overriddenData) => {
    setLinkedOverwrittenFormData(overriddenData);
    setShowLinkedDealOverridePopup(false);

    // Important: Call handleActualSubmit with the overridden data
    handleActualSubmit(overriddenData);
  };

  useEffect(() => {
    const fetchLists = async () => {
      axios({
        method: "post",
        url: "/api/GetProducts",
        data: {
          pathname: pathname,
          Business_Unit_Id: selectedBusinessUnit
        },
        signal: abortController.signal
      })
        .then((response) => {
          const { status, data } = response;

          if (status === 200) {
            const productData = data?.product_data?.rows || [];
            setProductData(productData);
          }
        })
        .catch((error) => {
          console.log("Get Products Api: ", error);

          if (axios.isCancel(error) || error.code === "ERR_CANCELED") {
            return;
          }

          const status = error?.response?.status;

          if (status === 403) {
            triggerSessionExpire();
          }
          else {
            setAlertMessage(
              status === 401
                ? "Unauthorized access. You do not have the required permissions to perform this action."
                : status === 429
                  ? "Request limit exceeded. Your account is temporarily disabled. Please contact the site administrator."
                  : "An error occurred while processing your request. Please try again later or contact the site administrator."
            );
            setAlertType("error");
            setShowAlert(true);

            if (status === 429) {
              setTimeout(logOut, 3000);
            }
          }
        });

      axios({
        method: "post",
        url: "/api/GetPaymentTermType",
        data: {
          pathname: pathname,
          Business_Unit_Id: selectedBusinessUnit,
        },
        signal: abortController.signal
      })
        .then((response) => {
          const { status, data } = response;

          if (status === 200) {
            setPaymentTermTypes(data || []);
          }
        })
        .catch((error) => {
          console.log("Get Payment Terms Api: ", error);

          if (axios.isCancel(error) || error.code === "ERR_CANCELED") {
            return;
          }

          const status = error?.response?.status;

          if (status === 403) {
            triggerSessionExpire();
          }
          else {
            setAlertMessage(
              status === 401
                ? "Unauthorized access. You do not have the required permissions to perform this action."
                : status === 429
                  ? "Request limit exceeded. Your account is temporarily disabled. Please contact the site administrator."
                  : "An error occurred while processing your request. Please try again later or contact the site administrator."
            );
            setAlertType("error");
            setShowAlert(true);

            if (status === 429) {
              setTimeout(logOut, 3000);
            }
          }
        });
    }
    fetchLists();

    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, []);

  useEffect(() => {
    if (selectedDeal) {
      setSelectedDealForAutopopulate(selectedDeal);
      setIsExistingDealHeader(false);
      setIsExistingBuyingLeg(false);
      setIsExistingSellingLeg(false);
      setIsLinkedThrougRoute(Boolean(Number(selectedDeal?.is_route_linked_deal)))

    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDeal?.id]);

  useEffect(() => {
    if (!linkedDealInitialFormData?.Deal_PFI_Id) {
      return; // No linked deal data yet
    }

    const currentSeller = linkedDealFormData?.Seller_Id;

    // ── Seller_Id fallback logic ────────────────────────────────────
    let seller = currentSeller;

    // Apply schema fallback only for non-Vantira deals
    const shouldApplySellerFallback = !selectedDeal?.is_vantira_deal;
    if (shouldApplySellerFallback && !seller) {
      seller = linkedDealFormDataSchema?.Seller_Id;
    }

    if (!seller && supplierCounterParties?.length > 0 && shouldApplySellerFallback) {
      const engro = supplierCounterParties.find(
        (cp) => cp?.value === "ENGRO EXIMP FZE"
      );
      if (engro) {
        seller = engro;
      }
    }

    // Only update if seller actually changed
    if (seller && JSON.stringify(seller) !== JSON.stringify(currentSeller)) {
      const updatedLinkedDealData = {
        ...linkedDealFormData,
        Seller_Id: seller,
      };

      setLinkedDealInitialFormData(structuredClone(updatedLinkedDealData));
      setLinkedDealFormData(structuredClone(updatedLinkedDealData));
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    linkedDealInitialFormData?.Deal_PFI_Id,
    selectedDeal?.is_vantira_deal,
    supplierCounterParties,
  ]);

  // New useEffect that runs when customerCounterParties is populated
  useEffect(() => {
    // Wait until we have both data loaded and customer counter parties available
    if (customerCounterParties?.length === 0) {
      return;
    }

    // Skip for vantira deals linked through route
    if (!selectedDeal?.is_vantira_deal && isLinkedThrougRoute && (isDealHeaderFormDataLoaded && !dealHeaderFormData?.Buyer_Id?.id)) {
      // Apply buyer fallback logic
      const currentBuyer = dealHeaderInitialFormData?.Buyer_Id;
      let buyer = currentBuyer;

      if (!buyer) {
        const vantiraEdge = customerCounterParties?.find(
          (cp) => cp?.value?.toLowerCase() === "vantira edge".toLowerCase()
        );
        if (vantiraEdge) {
          buyer = vantiraEdge;
        }
      }


      if (buyer && JSON.stringify(buyer) !== JSON.stringify(currentBuyer)) {
        const updatedformattedDealHeaderData = {
          ...dealHeaderInitialFormData,
          Buyer_Id: buyer,
        };
        const formattedSellingLegDataWithBuyerAddress = {
          ...sellingLegFormData,
          Customer_Address: buyer?.Address
        };
        setDealHeaderFormData(updatedformattedDealHeaderData);
        setSellingLegFormData(formattedSellingLegDataWithBuyerAddress);
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    dealHeaderInitialFormData,
    isDealHeaderFormDataLoaded,
    selectedDeal?.is_vantira_deal,
    customerCounterParties,
    isLinkedThrougRoute
  ]);


  useEffect(() => {
    const getPreShipmentDealData = async (selectedPFIId) => {
      try {
        setIsLoading(true);
        setShowAlert(false);
        setAlertMessage("");
        setAlertType("");

        const dealHeaderApiEndPoint = isFertilizersAndPetroChemicalsDeal
          ? "/api/GetOrderDetailsFertilizersAndPetroChemicals"
          : isFoodsPolymersOrSBLCDeal
            ? "/api/GetOrderDetailsFoodsPolymersAndSBLC"
            : null;

        const promises = [
          dealHeaderApiEndPoint && axios.post(dealHeaderApiEndPoint, {
            Business_Unit_Id: selectedBusinessUnit,
            Deal_PFI_Id: selectedPFIId,
            is_sblc_deal: selectedBusinessUnit === 6
          }),
          axios.post("/api/GetBuyingLeg", { Business_Unit_Id: selectedBusinessUnit, Deal_PFI_Id: selectedPFIId }),
          axios.post("/api/GetSellingLeg", { Business_Unit_Id: selectedBusinessUnit, Deal_PFI_Id: selectedPFIId })
        ];

        if (isSBLCDeal) {
          promises.push(axios.post("/api/GetDealSBLCProductGrades", { Deal_PFI_Id: selectedPFIId }));
        }

        const results = await Promise.all(promises);

        const isEmpty = (obj) => !obj || Object.keys(obj).length === 0;

        let rawDealHeaderData = results[0];
        let rawBuyingLegData = results[1];
        let rawSellingLegData = results[2];
        let rawSBLCProductGradeData = isSBLCDeal ? results[3] : { data: sblcProductGradeDataSchema };

        // Check if linked through route
        let linkedThroughRoute = Boolean(Number(selectedDeal?.is_route_linked_deal));

        let linkedDealDetails = null;

        if (linkedThroughRoute) {
          let linkedEndpoint = null;

          if (isFertilizersAndPetroChemicalsDeal) {
            linkedEndpoint = "/api/GetLinkedDealOrderDetailsFertilizersAndPetroChemicals";
          } else if (isFoodsPolymersOrSBLCDeal) {
            linkedEndpoint = "/api/GetLinkedDealOrderDetailsFoodsPolymersAndSBLC";
          }

          if (linkedEndpoint) {
            try {
              const linkedResponse = await axios.post(linkedEndpoint, {
                Business_Unit_Id: selectedBusinessUnit,
                Deal_PFI_Id: selectedPFI?.id,
              });
              linkedDealDetails = linkedResponse?.data?.linked_deal_details || null;
            } catch (linkedError) {
              console.log("Linked deal API failed:", linkedError);
            }
          }
        }

        const dealHeaderData = isEmpty(rawDealHeaderData?.data) ? dealHeaderDataSchema : rawDealHeaderData?.data;
        const buyingLegData = isEmpty(rawBuyingLegData?.data) ? buyingLegDataSchema : rawBuyingLegData?.data;
        const sellingLegData = isEmpty(rawSellingLegData?.data) ? sellingLegDataSchema : rawSellingLegData?.data;
        const sblcProductGradeData = isEmpty(rawSBLCProductGradeData?.data) ? sblcProductGradeDataSchema : rawSBLCProductGradeData?.data;

        const formattedDealHeaderData = formatDateFields(dealHeaderData, [
          "Booked_for_Month",
          "Order_Date",
          "Deal_Close_Date",
          "Deal_Sheet_Approval_Date",
          "Shipment_Month_And_Date"
        ]);
        const formattedBuyingLegData = formatDateFields(buyingLegData, [
          "PO_Date",
          "Latest_Shipment_Date",
          "LC_Expiry_Date",
          "LC_Issue_Date",
          "Supplier_CI_Date",
          "Supplier_PFI_Date"
        ]);
        const formattedSellingLegData = formatDateFields(sellingLegData, [
          "Last_Shipment_Date",
          "PFI_Issuance_Date",
          "PFI_Expiry_Date",
          "Issuance_Date",
          "LC_Expiry_Date"
        ]);

        if (selctedPFIForAutoPopulate) {
          formattedDealHeaderData.Deal_PFI_Id = null;
          formattedBuyingLegData.Deal_PFI_Id = null;
          formattedSellingLegData.Deal_PFI_Id = null;

          const updatedDealHeaderFormData = { ...dealHeaderFormData, Deal_PFI_Id: null };
          const updatedBuyingLegFormData = { ...buyingLegFormData, Deal_PFI_Id: null };
          const updatedSellingLegFormData = { ...sellingLegFormData, Deal_PFI_Id: null };
          const updatedSblcFormData = { ...sblcFormData, Deal_PFI_Id: null };
          setDealHeaderInitialFormData(updatedDealHeaderFormData);
          setBuyingLegInitialFormData(updatedBuyingLegFormData);
          setSellingLegInitialFormData(updatedSellingLegFormData);
          setSBLCInitialFormData(updatedSblcFormData);
        } else {
          setDealHeaderInitialFormData(structuredClone(formattedDealHeaderData));
          setBuyingLegInitialFormData(structuredClone(formattedBuyingLegData));
          setSellingLegInitialFormData(structuredClone(formattedSellingLegData));
          setSBLCInitialFormData(structuredClone(sblcProductGradeData));
        }
        setDealHeaderFormData(structuredClone(formattedDealHeaderData));
        setIsDealHeaderFormDataLoaded(true);
        setSellingLegFormData(structuredClone(formattedSellingLegData));
        setBuyingLegFormData(structuredClone(formattedBuyingLegData));
        setSBLCFormData(structuredClone(sblcProductGradeData));
        // Set the linked deal form data
        if (linkedDealDetails) {
          setLinkedDealInitialFormData(structuredClone(linkedDealDetails));
          setLinkedDealFormData(structuredClone(linkedDealDetails));
        } else {

          setIsExistingLinkedDeal(false);
          setLinkedDealInitialFormData(structuredClone(linkedDealFormDataSchema));
          setLinkedDealFormData(structuredClone(linkedDealFormDataSchema));
        }

      } catch (error) {
        console.log("Get Pre-Shipment Deal Data Api: ", error);
        handleApiError(error);
      } finally {
        setIsLoading(false);
      }
    };

    const fetchPriodLockFields = async () => {
      axios({
        method: "post",
        url: "/api/GetPeriodLockFields",
        data: {
          pathname,
          Business_Unit_Id: selectedBusinessUnit,
        },
        signal: abortController.signal
      })
        .then((response) => {
          setIsLoading(false);
          const { status, data } = response;

          if (status === 200) {
            setLockedFields(data?.data);
          }
        })
        .catch((error) => {
          console.log("Period Lock Field Api: ", error);

          if (axios.isCancel(error) || error.code === "ERR_CANCELED") {
            return;
          }

          const status = error?.response?.status;

          if (status === 403) {
            triggerSessionExpire();
          }
          else {
            setAlertMessage(
              status === 401
                ? "Unauthorized access. You do not have the required permissions to perform this action."
                : status === 429
                  ? "Request limit exceeded. Your account is temporarily disabled. Please contact the site administrator."
                  : "An error occurred while processing your request. Please try again later or contact the site administrator."
            );
            setAlertType("error");
            setShowAlert(true);

            if (status === 429) {
              setTimeout(logOut, 3000);
            }
          }
        });
    }

    const checkPeriodLockStatus = async () => {
      axios({
        method: "post",
        url: "/api/GetDealPFIPeriodLockStatus",
        data: {
          pathname,
          Business_Unit_Id: selectedBusinessUnit,
          deal_pfi_id: selectedPFI?.id
        },
        signal: abortController.signal
      })
        .then((response) => {
          setIsLoading(false);
          const { status, data } = response;

          if (status === 200) {
            setIsPeriodLocked(data?.period_lock);
          }
        })
        .catch((error) => {
          console.log("Period Lock Status Field Api: ", error);

          if (axios.isCancel(error) || error.code === "ERR_CANCELED") {
            return;
          }

          const status = error?.response?.status;

          if (status === 403) {
            triggerSessionExpire();
          }
          else {
            setAlertMessage(
              status === 401
                ? "Unauthorized access. You do not have the required permissions to perform this action."
                : status === 429
                  ? "Request limit exceeded. Your account is temporarily disabled. Please contact the site administrator."
                  : "An error occurred while processing your request. Please try again later or contact the site administrator."
            );
            setAlertType("error");
            setShowAlert(true);

            if (status === 429) {
              setTimeout(logOut, 3000);
            }
          }
        });
    };

    const selectedPFIId = selctedPFIForAutoPopulate?.id || selectedPFI?.id;
    if (selectedPFIId) {
      fetchPriodLockFields();
      getPreShipmentDealData(selectedPFIId);
      checkPeriodLockStatus()
      setIsDealHeaderFormDataLoaded(false);
    }

    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [selectedPFI?.id, selctedPFIForAutoPopulate]);


  useEffect(() => {
    const fetchPreShipmentListData = async () => {
      setIsPreShipmentListDataLoading(true);

      try {
        await dispatch(fetchPreShipmentLists({ pathname, businessUnitId: selectedBusinessUnit })).unwrap();
      }
      catch (error) {
        console.error("Failed to fetch pre-shipment lists:", error);
      }
      finally {
        setIsPreShipmentListDataLoading(false);
      }
    }

    if (selectedBusinessUnit) {
      fetchPreShipmentListData();
    }

    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [selectedBusinessUnit]);

  useEffect(() => {
    const fetchEntitiesList = async () => {

      setSelectedEntity(null);
      setIsEntitiesLoading(true);
      try {
        const response = await axios({
          method: "post",
          url: "/api/GetEntity",
          data: {
            business_unit_id: selectedBusinessUnit,
          },
          signal: abortController.signal
        });

        if (response.status === 200) {
          const apiEntities = response.data?.data || [];

          setEntitiesForCopy(apiEntities);
        } else {
          setAlertMessage("Failed to load entities.");
          setAlertType("error");
          setShowAlert(true);
        }
      } catch (error) {
        if (axios.isCancel(error)) return;
        console.error("GetDealListEntity API error:", error);
        setAlertMessage("Error loading entities for copy.");
        setAlertType("error");
        setShowAlert(true);
      } finally {
        setIsEntitiesLoading(false);
      }
    }

    fetchEntitiesList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    const fetchDealsList = async () => {
      const entityId = selectedEntity?.id || null;
      setisDealsListForCopyLoading(true);
      setShowAlert(false);
      setAlertMessage("");
      setAlertType("");

      try {
        const response = await axios({
          method: "post",
          url: "/api/GetDealListForAutoPopulate",
          data:
          {
            pathname,
            Business_Unit_Id: selectedBusinessUnit,
            entity_id: entityId

          },
          signal: abortController.signal,
        });

        setisDealsListForCopyLoading(false);
        const { status, data } = response;

        if (status === 200) {
          setDealListForAutoPopulate(data?.deals || []);
        } else {
          setAlertMessage("An error occurred while fetching deals.");
          setAlertType("error");
          setShowAlert(true);
        }
      } catch (error) {
        console.error("Get Deals List API: ", error);
        setisDealsListForCopyLoading(false);

        if (axios.isCancel(error) || error.code === "ERR_CANCELED") return;

        const status = error?.response?.status;

        if (status === 403) {
          triggerSessionExpire();
        } else {
          setAlertMessage(
            status === 401
              ? "Unauthorized access."
              : status === 429
                ? "Request limit exceeded."
                : "An error occurred while fetching deals."
          );
          setAlertType("error");
          setShowAlert(true);

          if (status === 429) {
            setTimeout(logOut, 3000);
          }
        }
      }
    };

    if (selectedBusinessUnit && selectedEntity?.id && isWritable) {
      fetchDealsList();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedEntity?.id, selectedBusinessUnit, isWritable]);

  const fetchPFIsForCopy = async (deal = null, setPfiList = [], setSelectedCurrentPfi) => {
    if (!deal) {
      setPfiList([]);
      return;
    }
    setisPFIsListForCopyLoading(true)
    setShowAlert(false);
    setAlertMessage("");
    setAlertType("");

    try {
      const response = await axios({
        method: "post",
        url: "/api/GetDealPFIListForAutoPopulate",
        data:
        {
          pathname,
          Deal_Id: deal?.id,
          Business_Unit_Id: selectedBusinessUnit

        },
        signal: abortController.signal,
      });

      const { status, data } = response;
      setisPFIsListForCopyLoading(false)
      if (status === 200) {
        if (setSelectedCurrentPfi) {
          setSelectedCurrentPfi(null)
          let filteredPFIs = data?.deal_pfi?.filter(pfi => pfi.id !== selctedPFIForAutoPopulate?.id) || [];
          setPfiList(filteredPFIs);
        } else {
          setPfiList(data?.deal_pfi || []);
        }
      } else {
        setAlertMessage("An error occurred while fetching PFIs for auto-populate.");
        setAlertType("error");
        setShowAlert(true);
      }
    } catch (error) {
      setisPFIsListForCopyLoading(false)
      console.error("Get Deal PFI List API: ", error);
      if (axios.isCancel(error) || error.code === "ERR_CANCELED") return;

      const status = error?.response?.status;
      if (status === 403) {
        triggerSessionExpire();
      } else {
        setAlertMessage(
          status === 401
            ? "Unauthorized access."
            : status === 429
              ? "Request limit exceeded."
              : "An error occurred while fetching PFIs."
        );
        setAlertType("error");
        setShowAlert(true);

        if (status === 429) {
          setTimeout(logOut, 3000);
        }
      }
    }
  };

  useEffect(() => {
    if (selectedDeal?.id && isWritable) {
      fetchPFIsForCopy(selectedDeal, setSelectedDealPfiList, null);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDeal?.id, isWritable]);


  useEffect(() => {
    if (selectedDealForAutopopulate?.id && isWritable) {
      fetchPFIsForCopy(selectedDealForAutopopulate, setPFIsForCopy, setSelctedPFIForAutoPopulate);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDealForAutopopulate?.id, isWritable]);

  useEffect(() => {
    const fetchLinkedDealPFIs = async () => {
      if (!selectedLinkedDeal?.id || !isWritable) {
        setSelectedLinkedDealPfiList([]);
        return;
      }

      setShowAlert(false);
      setAlertMessage("");
      setAlertType("");

      try {
        const response = await axios({
          method: "post",
          url: "/api/GetDealPFIListForAutoPopulate",
          data: {
            pathname,
            Deal_Id: selectedLinkedDeal.id,
            Business_Unit_Id: selectedBusinessUnit
          },
          signal: abortController.signal,
        });

        const { status, data } = response;

        if (status === 200) {
          setSelectedLinkedDealPfiList(data?.deal_pfi || []);
        } else {
          setAlertMessage("An error occurred while fetching linked deal PFIs.");
          setAlertType("error");
          setShowAlert(true);
        }
      } catch (error) {
        console.error("Get Linked Deal PFI List API: ", error);

        if (axios.isCancel(error) || error.code === "ERR_CANCELED") return;

        const status = error?.response?.status;

        if (status === 403) {
          triggerSessionExpire();
        } else {
          setAlertMessage(
            status === 401
              ? "Unauthorized access."
              : status === 429
                ? "Request limit exceeded."
                : "An error occurred while fetching linked deal PFIs."
          );
          setAlertType("error");
          setShowAlert(true);

          if (status === 429) {
            setTimeout(logOut, 3000);
          }
        }
      } finally {
      }
    };

    fetchLinkedDealPFIs();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLinkedDeal?.id, isWritable]);

  // Add this useEffect to fetch linked deal PFI list when selectedLinkedDeal changes
  useEffect(() => {
    const fetchLinkedDealPFIs = async () => {
      if (!selectedLinkedDeal?.id || !isWritable) {
        setSelectedLinkedDealPfiList([]);
        return;
      }

      setShowAlert(false);
      setAlertMessage("");
      setAlertType("");

      try {
        const response = await axios({
          method: "post",
          url: "/api/GetDealPFIListForAutoPopulate",
          data: {
            pathname,
            Deal_Id: selectedLinkedDeal.id,
            Business_Unit_Id: selectedBusinessUnit
          },
          signal: abortController.signal,
        });

        const { status, data } = response;

        if (status === 200) {
          setSelectedLinkedDealPfiList(data?.deal_pfi || []);
        } else {
          setAlertMessage("An error occurred while fetching linked deal PFIs.");
          setAlertType("error");
          setShowAlert(true);
        }
      } catch (error) {
        console.error("Get Linked Deal PFI List API: ", error);

        if (axios.isCancel(error) || error.code === "ERR_CANCELED") return;

        const status = error?.response?.status;

        if (status === 403) {
          triggerSessionExpire();
        } else {
          setAlertMessage(
            status === 401
              ? "Unauthorized access."
              : status === 429
                ? "Request limit exceeded."
                : "An error occurred while fetching linked deal PFIs."
          );
          setAlertType("error");
          setShowAlert(true);

          if (status === 429) {
            setTimeout(logOut, 3000);
          }
        }
      } finally {
      }
    };

    fetchLinkedDealPFIs();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLinkedDeal?.id, selectedBusinessUnit, isWritable]);

  // Add this useEffect to auto-match and set linked PFI when selectedPFI changes
  useEffect(() => {
    if (!selectedPFI?.value || !selectedLinkedDealPfiList.length) {
      return;
    }

    // Extract the last digit/character from selectedPFI value (e.g., "VTE-FT-1990-2026C" -> "C")
    const selectedPFILastChar = selectedPFI.value.slice(-1);

    // Find matching PFI in linked deal list based on last character
    const matchingLinkedPFI = selectedLinkedDealPfiList.find(pfi => {
      const linkedPFILastChar = pfi.value?.slice(-1);
      return linkedPFILastChar === selectedPFILastChar;
    });

    // Set the matched linked PFI if found
    if (matchingLinkedPFI) {
      setSelectedLinkedPFI(matchingLinkedPFI);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPFI?.value, selectedLinkedDealPfiList]);

  useEffect(() => {
    setIsUpdated(
      isDealHeaderFormUpdated ||
      isBuyingLegFormUpdated ||
      isSellingLegFormUpdated ||
      isSBLCFormUpdated ||
      isLinkedDealFormUpdated ||
      isBuyingPaymentTermsFormUpdated ||      // ← add
      isSellingPaymentTermsFormUpdated        // ← add
    );
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [isDealHeaderFormUpdated, isBuyingLegFormUpdated, isSellingLegFormUpdated, isSBLCFormUpdated, isLinkedDealFormUpdated, isBuyingPaymentTermsFormUpdated, isSellingPaymentTermsFormUpdated]);

  useEffect(() => {

    const isEmpty = typeof dealHeaderFormData === "object" && !dealHeaderFormData?.Deal_PFI_Id;

    if (!isExistingDealHeader) {
      if (!isEmpty) {
        setIsExistingDealHeader(true);
        setIsExistingLinkedDeal(true);
      }
      else {
        setIsExistingDealHeader(false);
      }
    }

    if (!isDealHeaderFormSavedOrDeleted && isWritable) {
      const isDealHeaderFormUpdated = checkIfFormUpdated(
        dealHeaderFormData,
        dealHeaderInitialFormData,
        ["Booked_for_Month", "Order_Date", "Deal_Close_Date", "Deal_Sheet_Approval_Date", "Shipment_Month_And_Date"]
      );
      setIsDealHeaderFormUpdated(isDealHeaderFormUpdated);
    }

    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [dealHeaderFormData, selctedPFIForAutoPopulate, isNewPFI]);

  useEffect(() => {

    const isEmpty = typeof buyingLegFormData === "object" && !buyingLegFormData?.Deal_PFI_Id;

    if (!isExistingBuyingLeg) {
      if (!isEmpty) {
        setIsExistingBuyingLeg(true);
        setIsExistingLinkedDeal(true);
      }
      else {
        setIsExistingBuyingLeg(false);
      }
    }

    if (!isBuyingLegFormSavedOrDeleted) {
      const isBuyingLegFormUpdated = checkIfFormUpdated(buyingLegFormData, buyingLegInitialFormData);
      setIsBuyingLegFormUpdated(isBuyingLegFormUpdated);
    }

    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [buyingLegFormData, selctedPFIForAutoPopulate, isNewPFI]);

  useEffect(() => {

    const isEmpty = typeof sellingLegFormData === "object" && !sellingLegFormData?.Deal_PFI_Id;

    if (!isExistingSellingLeg) {
      if (!isEmpty) {
        setIsExistingSellingLeg(true);
        setIsExistingLinkedDeal(true);
      }
      else {
        setIsExistingSellingLeg(false);
      }
    }

    if (!isSellingLegFormSavedOrDeleted) {
      const isSellingLegFormUpdated = checkIfFormUpdated(
        sellingLegFormData,
        sellingLegInitialFormData,
        ["PFI_Issuance_Date", "PFI_Expiry_Date", "Last_Shipment_Date", "Issuance_Date"]
      );
      setIsSellingLegFormUpdated(isSellingLegFormUpdated);
    }

    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [sellingLegFormData, selctedPFIForAutoPopulate, isNewPFI]);

  useEffect(() => {

    if (!isLinkedThrougRoute || !isWritable) {
      setIsLinkedDealFormUpdated(false);
      return;
    }

    const isLinkedFormUpdated = checkIfFormUpdated(linkedDealFormData, linkedDealInitialFormData);
    setIsLinkedDealFormUpdated(isLinkedFormUpdated);

    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [linkedDealFormData, isLinkedThrougRoute, isWritable]);


  const resetLockedFields = (formData, lockedFields = [], subTab, tabName = "Pre-Shipment") => {
    // 1. Initial data setup
    const initialFormDataMap = {
      "Deal Header": dealHeaderInitialFormData,
      "Buying Leg": buyingLegInitialFormData,
      "Selling Leg": sellingLegInitialFormData,
      "SBLC Product Grade": sblcInitialFormData,
    };

    const initialFormData = initialFormDataMap[subTab] || formData;
    const clonedInitialFormData = initialFormData ? structuredClone(initialFormData) : {};
    const clonedInitialProductFormData = dealHeaderInitialFormData ? structuredClone(dealHeaderInitialFormData) : {};

    if (subTab === "SBLC Product Grade") {
      return clonedInitialFormData;
    }

    const relevantLockedFields = lockedFields.filter(field => field.tab === tabName);

    // Create a set of explicitly locked field names
    const lockedFieldNames = new Set(relevantLockedFields.map(field => field.field_name));

    // Define dependent fields
    const dependentFields = ["Total_Amount", "Freight", "FOB_Value"];
    if (relevantLockedFields.length > 0) {
      dependentFields.forEach(field => lockedFieldNames.add(field));
    }

    // 3. Prepare updated form data
    const updatedFormData = { ...formData };

    // 4. Handle non-products fields
    Object.keys(formData).forEach(key => {
      if (key !== "products" && lockedFieldNames.has(key)) {
        const initialValue = clonedInitialFormData[key];
        const isObject = initialValue != null && typeof initialValue === "object" && !Array.isArray(initialValue);
        updatedFormData[key] = clonedInitialFormData[key] !== undefined
          ? (isObject ? initialValue?.id ?? null : initialValue)
          : undefined;
      }
    });

    // 5. Handle products array
    if (formData.products && Array.isArray(formData.products)) {

      updatedFormData.products = clonedInitialProductFormData.products;
    }

    return updatedFormData;
  };

  useImperativeHandle(ref, () => ({
    handleSubmit: async () => {
      // Check if this is a linked deal that needs override
      // This check must happen BEFORE any actual submission
      if (isLinkedThrougRoute && !linkedOverwrittenFormData) {
        setShowLinkedDealOverridePopup(true);
        return false;
      }

      // If we have override data or not a linked deal, proceed with submission
      await handleActualSubmit(linkedOverwrittenFormData);
      return true; // Return true to indicate successful submission
    }
  }));

  const handleActualSubmit = async (linkedFormData = null) => {
    setIsLoading(true);
    setShowWarningDialog(false);

    try {
      const validationResults = await Promise.all([
        dealHeaderRef.current?.isValid() ?? false,
        buyingLegRef.current?.isValid() ?? false,
        sellingLegRef.current?.isValid() ?? false
      ]);

      if (validationResults.includes(false)) {
        setIsLoading(false);
        return;
      }

      const payload = {
        pathname,
        Business_Unit_Id: selectedBusinessUnit,
        deal_pfi_id: selectedPFI?.id
      };

      let isPfiLocked = true;

      if (!isPeriodLocked) {
        const response = await axios.post('/api/GetDealPFIPeriodLockStatus', payload);
        isPfiLocked = response.data?.period_lock;
      }

      // ── First submission: selected PFI (deal header + legs + payment terms) ──
      const submissionResults = await Promise.all([
        dealHeaderRef.current?.handleSubmit(undefined, dealHeaderFormData, isPfiLocked, isExistingDealHeader),
        buyingLegRef.current?.handleSubmit(undefined, buyingLegFormData, isPfiLocked, isExistingBuyingLeg),
        sellingLegRef.current?.handleSubmit(undefined, sellingLegFormData, isPfiLocked, isExistingSellingLeg),
        buyingPaymentTermsRef.current?.handleSubmit(isPfiLocked),
        sellingPaymentTermsRef.current?.handleSubmit(isPfiLocked),
      ]);

      if (!submissionResults.every(result => result === true)) {
        setIsLoading(false);
        return;
      }

      // ── Second submission: linked PFI ────────────────────────────────────────
      if (isLinkedThrougRoute && linkedFormData) {
        const originalDealHeaderData = { ...dealHeaderFormData };
        const originalBuyingLegData = { ...buyingLegFormData };
        const originalSellingLegData = { ...sellingLegFormData };

        const newBuyer = linkedFormData.Buyer_Id;
        const oldBuyerId = linkedDealInitialFormData?.Buyer_Id?.id;
        const newBuyerId = newBuyer?.id;
        const buyerHasChanged = newBuyerId && newBuyerId !== oldBuyerId;

        let overriddenSellingLegData;

        if (buyerHasChanged && newBuyer?.Address) {
          overriddenSellingLegData = {
            ...sellingLegFormData,
            Customer_Address: newBuyer.Address,
            Deal_PFI_Id: linkedDealFormData.Deal_PFI_Id,
          };
        } else {
          overriddenSellingLegData = {
            ...sellingLegFormData,
            Customer_Address: linkedDealFormData?.Customer_Address,
            Deal_PFI_Id: linkedDealFormData.Deal_PFI_Id,
          };
        }

        try {
          const overriddenDealHeaderData = {
            ...dealHeaderFormData,
            Deal_PFI_Id: linkedDealFormData.Deal_PFI_Id,
            Buyer_Id: newBuyer?.id || null,
            Seller_Id: linkedFormData.Seller_Id?.id || null,
            Buying_Payment_Terms_Id: linkedFormData?.Buying_Payment_Terms_Id?.id || null,
            Selling_Payment_Terms_Id: linkedFormData?.Selling_Payment_Terms_Id?.id || null,
            Buying_Tenor_Id: linkedFormData?.Buying_Tenor_Id?.id || null,
            Selling_Tenor_Id: linkedFormData?.Selling_Tenor_Id?.id || null,
            products: linkedFormData.products.map(product => ({
              ...product,
              Product_Id: product.Product_Id || null,
              ...(product.Product_Grade_Id && {
                Product_Grade_Id: product.Product_Grade_Id || null,
              }),
              Buying_Price: product.Buying_Price,
              Selling_Price: product.Selling_Price,
            })),
          };

          const overriddenBuyingLegData = {
            ...buyingLegFormData,
            Deal_PFI_Id: linkedDealFormData.Deal_PFI_Id,
          };

          await new Promise(resolve => setTimeout(resolve, 100));


          // Submit deal header + legs for linked PFI
          const secondSubmissionResults = await Promise.all([
            dealHeaderRef.current?.handleSubmit(undefined, overriddenDealHeaderData, isPfiLocked, isExistinglinkedDeal),
            buyingLegRef.current?.handleSubmit(undefined, overriddenBuyingLegData, isPfiLocked, isExistinglinkedDeal),
            sellingLegRef.current?.handleSubmit(undefined, overriddenSellingLegData, isPfiLocked, isExistinglinkedDeal),
          ]);

          if (!secondSubmissionResults.every(result => result === true)) {
            // Rollback is not feasible here (first submission already committed),
            // but we surface an error so the user is aware.
            throw new Error("Linked PFI submission partially failed.");
          }

          setLinkedOverwrittenFormData(null);

        } catch (error) {
          console.error('Error during linked PFI submission:', error);

          // Restore primary PFI form data to its pre-submit state
          setDealHeaderFormData(originalDealHeaderData);
          setBuyingLegFormData(originalBuyingLegData);
          setSellingLegFormData(originalSellingLegData);

          throw error;
        }
      }

      if (submissionResults.every(result => result === true)) {
        setActiveTab("Post-Shipment");
        setSelectedTab(null);
      }
    }
    catch (error) {
      console.error("Add/Update Pre-Shipment data api error:", error);
      setAlertMessage("An error occurred while submitting data. Please try again.");
      setAlertType("error");
      setShowAlert(true);
    }
    finally {
      setIsLoading(false);
      setSelctedPFIForAutoPopulate(null);
    }
  };

  const checkIfFormUpdated = (formData, initialFormData, dateKeys = []) => {
    // Check non-date fields
    const isNonDateUpdated = Object.keys(initialFormData).some((key) => {
      const currentValue = formData[key];
      const initialValue = initialFormData[key];

      return JSON.stringify(currentValue) !== JSON.stringify(initialValue);
    });

    // Check date fields separately
    const isDateUpdated = dateKeys.some((dateKey) => {
      const currentDate = formData[dateKey] ? dayjs(formData[dateKey]) : formData[dateKey];
      const initialDate = initialFormData[dateKey] ? dayjs(initialFormData[dateKey]) : initialFormData[dateKey];

      return JSON.stringify(currentDate) !== JSON.stringify(initialDate);
    });

    return isNonDateUpdated || isDateUpdated;
  }

  const handleApiError = (error) => {
    const status = error?.response?.status;
    const data = error?.response?.data;

    switch (status) {
      case 400:
        setAlertMessage(data?.error || "An error occurred while processing your request. Please try again later or contact the site administrator.");
        setAlertType("error");
        setShowAlert(true);
        break;
      case 403:
        triggerSessionExpire();
        break;
      case 401:
        setAlertMessage("Unauthorized access. You do not have the required permissions to perform this action.");
        setAlertType("error");
        setShowAlert(true);
        break;
      case 429:
        setAlertMessage("Request limit exceeded. Your account is temporarily disabled. Please contact the site administrator.");
        setAlertType("error");
        setShowAlert(true);
        setTimeout(logOut, 3000);
        break;
      default:
        setAlertMessage("An error occurred while processing your request. Please try again later or contact the site administrator.");
        setAlertType("error");
        setShowAlert(true);
        break;
    }
  }

  const handleExpandAll = () => {
    setExpandedFormSections([1, 2, 3, 4, 5, 6, 7]);
  }

  const handleCollapseAll = () => {
    setExpandedFormSections([]);
  }

  const handleSubmitWrapper = () => {
    if (isSBLCDeal && isExistingSBLC && isSBLCFormUpdated && dealHeaderFormData?.tranche_product_exists) {
      setWarningDialogText("Updating the SBLC will remove it from Shipment Details. Do you want to continue?");
      setShowWarningDialog(true);
    }
    else {
      ref?.current?.handleSubmit();
    }
  }

  const handleDelete = async () => {
    try {
      setIsLoading(true);
      setShowAlert(false);
      setAlertMessage("");
      setAlertType("");
      setShowDeleteModal(false);

      const payload = {
        pathname,
        Business_Unit_Id: selectedBusinessUnit,
        deal_pfi_id: selectedPFI?.id
      };

      let isPfiLocked = null;

      // Check period lock status if not already locked
      if (!isPeriodLocked) {
        const response = await axios.post('/api/GetDealPFIPeriodLockStatus', payload);
        isPfiLocked = response.data?.period_lock;
      }

      // Only proceed with deletion if isPfiLocked is false or 0
      if (isPfiLocked === true || isPfiLocked === 1) {
        setAlertMessage("Cannot delete: PFI is locked.");
        setAlertType("error");
        setShowAlert(true);
        return;
      }

      // Store original form data for rollback in case of error
      const originalDealHeaderData = { ...dealHeaderFormData };
      const originalBuyingLegData = { ...buyingLegFormData };
      const originalSellingLegData = { ...sellingLegFormData };

      try {
        // First deletion: Delete data for selected PFI
        await Promise.all([
          dealHeaderRef.current?.handleDelete(selectedPFI?.id),
          buyingLegRef.current?.handleDelete(selectedPFI?.id),
          sellingLegRef.current?.handleDelete(selectedPFI?.id),
        ]);

        // If linked deal exists, delete linked PFI data as well
        if (isLinkedThrougRoute && linkedDealFormData?.Deal_PFI_Id) {
          // Small delay to ensure state updates
          await new Promise(resolve => setTimeout(resolve, 100));
          // Second deletion: Delete data for linked PFI
          await Promise.all([
            dealHeaderRef.current?.handleDelete(linkedDealFormData.Deal_PFI_Id),
            buyingLegRef.current?.handleDelete(linkedDealFormData.Deal_PFI_Id),
            sellingLegRef.current?.handleDelete(linkedDealFormData.Deal_PFI_Id),
          ]);

          // Reset linked deal form data
          setLinkedDealInitialFormData(structuredClone(linkedDealFormDataSchema));
          setLinkedDealFormData(structuredClone(linkedDealFormDataSchema));
        }

        // Reset all form data after successful deletion
        setDealHeaderInitialFormData(structuredClone(dealHeaderDataSchema));
        setDealHeaderFormData(structuredClone(dealHeaderDataSchema));
        setBuyingLegInitialFormData(structuredClone(buyingLegDataSchema));
        setBuyingLegFormData(structuredClone(buyingLegDataSchema));
        setSellingLegInitialFormData(structuredClone(sellingLegDataSchema));
        setSellingLegFormData(structuredClone(sellingLegDataSchema));
        setSBLCInitialFormData(structuredClone(sblcProductGradeDataSchema));
        setSBLCFormData(structuredClone(sblcProductGradeDataSchema));

        // ── Reset payment terms ──────────────────────────────────────────
        buyingPaymentTermsRef.current?.handleReset();
        sellingPaymentTermsRef.current?.handleReset();

        // Show success message
        setAlertMessage(
          isLinkedThrougRoute && linkedDealFormData?.Deal_PFI_Id
            ? "Pre-shipment data deleted successfully for both selected and linked PFI."
            : "Pre-shipment data deleted successfully."
        );
        setAlertType("success");
        setShowAlert(true);
      } catch (deleteError) {
        console.error('Error during deletion:', deleteError);

        // Rollback to original data in case of error
        setDealHeaderFormData(originalDealHeaderData);
        setBuyingLegFormData(originalBuyingLegData);
        setSellingLegFormData(originalSellingLegData);

        throw deleteError;
      }
    } catch (error) {
      console.error("Error deleting Pre-Shipment data:", error);
      setAlertMessage("Failed to delete Pre-Shipment data.");
      setAlertType("error");
      setShowAlert(true);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCloseDialog = () => {
    setShowWarningDialog(false);
  }

  const handleCloseModal = () => {
    setShowDeleteModal(false);
  }

  return (
    <Grid
      container
      spacing={"10px"}
      className="input-fields align-items-start justify-content-between autocomplete"
    >
      <div className="w-100 d-flex justify-content-between align-items-center mt-2 flex-wrap row-gap-3">
        <div className="d-flex align-items-center gap-2 form-section-button">
          <Button
            variant="outlined"
            size="small"
            startIcon={<UnfoldMoreIcon />}
            disabled={expandedFormSections.length === 5}
            onClick={handleExpandAll}
          >
            Expand All
          </Button>

          <Button
            variant="outlined"
            size="small"
            startIcon={<UnfoldLessIcon />}
            disabled={expandedFormSections.length === 0}
            onClick={handleCollapseAll}
          >
            Collapse All
          </Button>
        </div>

        <div className="copy-pfi autocomplete flex-wrap">
          <VirtualizedAutocomplete
            isLoading={isEntitiesLoading}
            isMultiple={false}
            isObject={true}
            isWritable={isWritable}
            filterOn="Select Entity to Auto Populate"
            options={isEntitiesLoading ? [] : entitiesForCopy}
            selectedOptions={selectedEntity || null}
            handleSelectChange={(filterOn, newValue) => {
              setDealListForAutoPopulate([])
              setSelectedDealForAutopopulate(null);
              setSelectedEntity(newValue)
            }
            }
          />
          <VirtualizedAutocomplete
            isLoading={isDealsListForCopyLoading}
            isMultiple={false}
            isObject={true}
            isWritable={isWritable}
            filterOn="Select Deal to Auto Populate"
            options={dealListForAutoPopulate}
            selectedOptions={selectedDealForAutopopulate}
            handleSelectChange={(filterOn, newValue) => setSelectedDealForAutopopulate(newValue)}
          />
          <VirtualizedAutocomplete
            isLoading={isPFIsListForCopyLoading}
            isMultiple={false}
            isObject={true}
            isWritable={isWritable}
            filterOn="Select PFI to Auto Populate"
            options={pfisForCopy}
            selectedOptions={selctedPFIForAutoPopulate}
            handleSelectChange={(filterOn, newValue) => setSelctedPFIForAutoPopulate(newValue)}
          />
        </div>
      </div>
      <Grid size={{ xs: 12 }}>
        <Grid
          container
          spacing={"10px"}
          justifyContent={"flex-end"}
        >
          <div className="copy-pfi autocomplete flex-wrap">
            <VirtualizedAutocomplete
              isLoading={isPFIsListForCopyLoading}
              isMultiple={false}
              isObject={true}
              isWritable={isWritable}
              filterOn="Select PFI"
              options={selectedDealPfiList}
              selectedOptions={selectedPFI}
              handleSelectChange={(filterOn, newValue) => setSelectedPFI(newValue)}
            />
          </div>
        </Grid>
      </Grid>

      <Grid size={{ xs: 12 }}>
        <DealHeader
          ref={dealHeaderRef}
          isWritable={isWritable}
          listData={preShipmentListData}
          isListDataLoading={isPreShipmentListDataLoading}
          isFertilizersAndPetroChemicalsDeal={isFertilizersAndPetroChemicalsDeal}
          isFoodsOrPolymersDeal={isFoodsOrPolymersDeal}
          isFoodsPolymersOrSBLCDeal={isFoodsPolymersOrSBLCDeal}
          isSBLCDeal={isSBLCDeal}
          isExistingDealHeader={isExistingDealHeader}
          isExistingSBLC={isExistingSBLC}
          isFormSavedOrDeleted={isDealHeaderFormSavedOrDeleted}
          selectedBusinessUnit={selectedBusinessUnit}
          selectedPFI={selectedPFI?.id}
          selctedPFIForAutoPopulate={selctedPFIForAutoPopulate}
          formData={dealHeaderFormData}
          productData={productData}
          customerCounterParties={customerCounterParties}
          supplierCounterParties={supplierCounterParties}
          setCustomerCounterParties={setCustomerCounterParties}
          setSupplierCounterParties={setSupplierCounterParties}
          sellingLegFormData={sellingLegFormData}
          sblcInitialFormData={sblcInitialFormData}
          sblcFormData={sblcFormData}
          isPeriodLocked={isPeriodLocked}
          lockedFields={lockedFields}
          resetLockedFields={resetLockedFields}
          expandedFormSections={expandedFormSections}
          showDeleteModal={showDeleteModal}
          setIsExistingDealHeader={setIsExistingDealHeader}
          setIsExistingSBLC={setIsExistingSBLC}
          setIsFormUpdated={setIsDealHeaderFormUpdated}
          setIsSBLCFormUpdated={setIsSBLCFormUpdated}
          setIsFormSavedOrDeleted={setIsDealHeaderFormSavedOrDeleted}
          setFormData={setDealHeaderFormData}
          setSellingLegFormData={setSellingLegFormData}
          setSBLCFormData={setSBLCFormData}
          setWarningDialogText={setWarningDialogText}
          setShowWarningDialog={setShowWarningDialog}
          setShowDeleteModal={setShowDeleteModal}
          setExpandedFormSections={setExpandedFormSections}
          setShowAlert={setShowAlert}
          setAlertMessage={setAlertMessage}
          setAlertType={setAlertType}
          logOut={logOut}
        />
      </Grid>

      <Grid size={{ xs: 6 }}>
        <BuyingPaymentTerms
          ref={buyingPaymentTermsRef}
          isWritable={isWritable}
          isListDataLoading={isPreShipmentListDataLoading}
          paymentTermTypes={paymentTermTypes}
          dealHeaderFormData={dealHeaderFormData}
          listData={preShipmentListData}
          selectedPFI={selectedPFI?.id}
          selectedBusinessUnit={selectedBusinessUnit}
          isPeriodLocked={isPeriodLocked}
          lockedFields={lockedFields}
          expandedFormSections={expandedFormSections}
          autoPopulatePFI={selctedPFIForAutoPopulate?.id || null}
          setExpandedFormSections={setExpandedFormSections}
          setIsUpdated={setIsBuyingPaymentTermsFormUpdated}
          setShowAlert={setShowAlert}
          setAlertMessage={setAlertMessage}
          setAlertType={setAlertType}
          logOut={logOut}
        />
      </Grid>

      <Grid size={{ xs: 6 }}>
        <SellingPaymentTerms
          ref={sellingPaymentTermsRef}
          isWritable={isWritable}
          isListDataLoading={isPreShipmentListDataLoading}
          paymentTermTypes={paymentTermTypes}
          dealHeaderFormData={dealHeaderFormData}
          listData={preShipmentListData}
          selectedPFI={selectedPFI?.id}
          selectedBusinessUnit={selectedBusinessUnit}
          isPeriodLocked={isPeriodLocked}
          lockedFields={lockedFields}
          expandedFormSections={expandedFormSections}
          autoPopulatePFI={selctedPFIForAutoPopulate?.id || null}
          setExpandedFormSections={setExpandedFormSections}
          setIsUpdated={setIsSellingPaymentTermsFormUpdated}
          setShowAlert={setShowAlert}
          setAlertMessage={setAlertMessage}
          setAlertType={setAlertType}
          logOut={logOut}
        />
      </Grid>

      <Grid size={{ xs: 6 }}>
        <BuyingLeg
          ref={buyingLegRef}
          isWritable={isWritable}
          isListDataLoading={isPreShipmentListDataLoading}
          isExistingBuyingLeg={isExistingBuyingLeg}
          selectedBusinessUnit={selectedBusinessUnit}
          selectedPFI={selectedPFI?.id}
          listData={preShipmentListData}
          formData={buyingLegFormData}
          dealHeaderFormData={dealHeaderFormData}
          isPeriodLocked={isPeriodLocked}
          lockedFields={lockedFields}
          resetLockedFields={resetLockedFields}
          expandedFormSections={expandedFormSections}
          setIsExistingBuyingLeg={setIsExistingBuyingLeg}
          setIsUpdated={setIsBuyingLegFormUpdated}
          setIsFormSavedOrDeleted={setIsBuyingLegFormSavedOrDeleted}
          setFormData={setBuyingLegFormData}
          setDealHeaderFormData={setDealHeaderFormData}
          setExpandedFormSections={setExpandedFormSections}
          setShowDeleteModal={setShowDeleteModal}
          setShowAlert={setShowAlert}
          setAlertMessage={setAlertMessage}
          setAlertType={setAlertType}
          logOut={logOut}
        />
      </Grid>

      <Grid size={{ xs: 6 }}>
        <SellingLeg
          ref={sellingLegRef}
          isWritable={isWritable}
          isListDataLoading={isPreShipmentListDataLoading}
          isExistingSellingLeg={isExistingSellingLeg}
          selectedBusinessUnit={selectedBusinessUnit}
          selectedPFI={selectedPFI?.id}
          listData={preShipmentListData}
          formData={sellingLegFormData}
          dealHeaderFormData={dealHeaderFormData}
          isPeriodLocked={isPeriodLocked}
          lockedFields={lockedFields}
          resetLockedFields={resetLockedFields}
          expandedFormSections={expandedFormSections}
          setIsExistingSellingLeg={setIsExistingSellingLeg}
          setIsUpdated={setIsSellingLegFormUpdated}
          setIsFormSavedOrDeleted={setIsSellingLegFormSavedOrDeleted}
          setFormData={setSellingLegFormData}
          setDealHeaderFormData={setDealHeaderFormData}
          setExpandedFormSections={setExpandedFormSections}
          setShowDeleteModal={setShowDeleteModal}
          setShowAlert={setShowAlert}
          setAlertMessage={setAlertMessage}
          setAlertType={setAlertType}
          logOut={logOut}
        />
      </Grid>

      <div className="d-flex gap-2 pt-3 submit-buttons">
        <button
          type="button"
          disabled={!isWritable || isLoading}
          className="btn btn-secondary custom-btn"
          onClick={handleSubmitWrapper}
        >
          {isExistingDealHeader || isExistingBuyingLeg || isExistingSellingLeg ? "Update" : "Save"}
        </button>

        {(isExistingDealHeader || isExistingBuyingLeg || isExistingSellingLeg) && (
          <Tooltip
            title={
              isPeriodLocked
                ? 'This deal is locked and cannot be deleted.'
                : ''
            }
          >
            <span>
              <button
                type="button"
                disabled={!isWritable || isLoading || isPeriodLocked}
                className="btn custom-btn btn-danger"
                onClick={() => setShowDeleteModal(true)}
              >
                Delete
              </button>
            </span>
          </Tooltip>
        )}
      </div>

      <Dialog open={showWarningDialog} onClose={handleCloseDialog}>
        <DialogContent>
          <DialogContentText className="text-center">
            {warningDialogText}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          {isSBLCDeal ? (
            <button
              type="button"
              className="btn btn-primary custom-btn"
              onClick={() => ref?.current?.handleSubmit()}
            >
              Yes
            </button>
          ) : (
            <button
              type="button"
              className="btn btn-primary custom-btn"
              onClick={handleCloseDialog}
            >
              Ok
            </button>
          )}
        </DialogActions>
      </Dialog>

      <Modal show={showDeleteModal} onHide={handleCloseModal} centered className="delete-modal">
        <Modal.Header style={{ padding: '7px 16px' }} closeButton>
          <Modal.Title>Are you sure?</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <span>
            {isLinkedThrougRoute && linkedDealFormData?.Deal_PFI_Id ?
              <>
                This will also delete the linked tranche data. Do you really want to delete?
              </>
              :
              <>
                Do you really want to delete the form details?
              </>
            }
            {isSBLCDeal && (
              <>
                <span>{" "}Deleting this will also remove the selected product grades.</span>
                <br />
              </>
            )}
            {" "}This action cannot be undone.
          </span>
        </Modal.Body>
        <Modal.Footer className="gap-2">
          <Button variant="outlined" size="small" onClick={handleCloseModal}>
            Cancel
          </Button>
          <Button variant="contained" size="small" onClick={handleDelete}>
            Delete
          </Button>
        </Modal.Footer>
      </Modal>

      <LinkedDealOverridePopup
        open={showLinkedDealOverridePopup}
        onClose={() => setShowLinkedDealOverridePopup(false)}
        onSubmit={handleLinkedDealOverrideSubmit}
        isFoodsPolymersOrSBLCDeal={isFoodsPolymersOrSBLCDeal}
        linkedDealFormData={linkedDealFormData}
        dealHeaderFormData={dealHeaderFormData}
        paymentTermTypes={paymentTermTypes}
        productData={productData}
        isListDataLoading={isPreShipmentListDataLoading}
        paymentTerms={preShipmentListData?.paymentTerms || []}
        tenors={preShipmentListData?.tenors || []}
        isPeriodLocked={isPeriodLocked}
        lockedFields={lockedFields}
        isWritable={isWritable}
        selectedBusinessUnit={selectedBusinessUnit}
        selectedPFI={selectedPFI}
        setShowAlert={setShowAlert}
        setAlertMessage={setAlertMessage}
        setAlertType={setAlertType}
        setLinkedDealFormData={setLinkedDealFormData}
        logOut={logOut}
      />
    </Grid >
  );
});

export default memo(PreShipment);